import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { Icons } from '../../../../shared/helpers/icons';
import { IRmsPlanReviewSchedule } from '../../../../shared/models/rmsPlanReviewSchedule';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { ReviewerInfoComponent } from "./reviewer-info/reviewer-info.component";
import { RMSRecordReviewService } from '../../../../shared/services/rmsRecordReview.service';
import { ProgressNotesComponent } from "./progress-notes/progress-notes.component";
import { RmsReviewOfficialSignatureComponent } from "./rms-review-official-signature/rms-review-official-signature.component";
import { RmsReviewSubmissionComponent } from './rms-review-submission/rms-review-submission.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'rms-record-review',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LucideAngularModule,
    PopupMessageComponent,
    SpinnerComponent,
    ReviewerInfoComponent,
    ProgressNotesComponent,
    RmsReviewOfficialSignatureComponent,
    RmsReviewSubmissionComponent
],
  templateUrl: './rms-record-review.component.html',
  styleUrl: './rms-record-review.component.css'
})
export class RmsRecordReviewComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentRmsRecordReview: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'info_section_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'review_notes_section_completion',
      nextStepDependency: 'info_section_completion'
    },
    {
      id: 3,
      completionProperty: 'official_signatures_completion',
      nextStepDependency: 'review_notes_section_completion'
    },
  ];
  finalStepConfig = {
      id: 4,
      completionProperty: 'rms_record_review_completion',
      nextStepDependency: 'official_signatures_completion'
  };
  steps = [1, 2, 3];
  currentStep: number = 1;
  tooltips = ['Reviewer Information', 'Review of Response, Management, and Support (RMS) Plan', 'Official Signature', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataservice: UserDataService,
    private rmsRecordReviewService: RMSRecordReviewService,
    private toastr: ToastrService
  ) {}

  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataservice.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentCaseId = params.get('caseId');
    });
    
    this.loadRmsRecordReview();
  }

  loadRmsRecordReview(): void {
    if (this.currentCaseId) {
      this.loading = true;
      this.rmsRecordReviewService.getRmsRecordReviewByCaseId(this.currentCaseId).subscribe({
        next: (response) => {
          if (Object.keys(response).length === 0) {
            this.currentRmsRecordReview = null;
            this.loading = false;
            return
          } else {
            this.currentRmsRecordReview = response;
            this.loading = false;
            return
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });
    }
  }

  handleUpdateRoarAssignmentForm(data: IRmsPlanReviewSchedule, section: number): void {
    if (this.currentCaseId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.rmsRecordReviewService.updateRmsRecordReview(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadRmsRecordReview();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 4 ) {
            this.router.navigate(['/monitoring/']);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating rms record of review:', error);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/monitoring/']);
  }

  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentRmsRecordReview) {
      return this.currentRmsRecordReview?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

}
